import { EMode } from "@components/shared/components/features/event-scheduler/enums/event.enums";
import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  step: 0,
  client: null,
  skipClientSelection: false,
  quote: null,
  quoteMode: null,
  selectedService: null,
  scopeOfWork: null,
  eventScheduler: null,
  workforce: {
    employees: [],
    contractors: [],
    supervisors: [],
  },
  errors: [],
  alias: "",
  description: "",
  notes: [],
  frequencyType: null,
  scheduleLater: false,
  selectedReminders: {
    admins: null,
    clients: null,
    workforce: null,
  },
  creditedDepositAmount: null,
  lastCreatedJobId: null,
  scratchCreateAutoFlow: null,
  editingFromSummary: false,
  returnToSummaryAfterEdit: false,
};

const jobCreateStepper = createSlice({
  name: "jobCreateStepper",
  initialState,
  reducers: {
    setClient: (state, action) => {
      state.client = action.payload;
    },
    setDescription: (state, action) => {
      state.description = action.payload;
    },
    setNotes: (state, action) => {
      state.notes = Array.isArray(action.payload) ? action.payload : [];
    },
    setFrequencyType: (state, action) => {
      state.frequencyType = action.payload;
      if (state.scopeOfWork) {
        if (Array.isArray(state.scopeOfWork)) {
          state.scopeOfWork = state.scopeOfWork.map((item) => ({ ...item, frequencyType: action.payload }));
        } else if (typeof state.scopeOfWork === "object") {
          state.scopeOfWork.frequencyType = action.payload;
        }
      }
      if (state.selectedService) {
        if (Array.isArray(state.selectedService)) {
          state.selectedService = state.selectedService.map((item) => ({
            ...item,
            frequencyType: action.payload,
            scopeOfWork: item.scopeOfWork ? { ...item.scopeOfWork, frequencyType: action.payload } : item.scopeOfWork,
          }));
        } else if (typeof state.selectedService === "object") {
          state.selectedService.frequencyType = action.payload;
          if (state.selectedService.scopeOfWork) {
            state.selectedService.scopeOfWork.frequencyType = action.payload;
          }
        }
      }
    },
    setSkipClientSelection: (state, action) => {
      state.skipClientSelection = action.payload;
    },

    setScopeOfWork: (state, action) => {
      state.scopeOfWork = action.payload;
    },

    setScopeOfWorkCostOverride: (state, action) => {
      if (!state.scopeOfWork) {
        state.scopeOfWork = {
          costOverride: action.payload,
        };
        return;
      }

      state.scopeOfWork = {
        ...state.scopeOfWork,
        costOverride: action.payload,
      };
    },

    clearScopeOfWorkCostOverride: (state) => {
      if (!state.scopeOfWork) return;

      state.scopeOfWork = {
        ...state.scopeOfWork,
        costOverride: null,
      };
    },

    setScopeOfWorkMode: (state, action) => {
      if (state.quoteMode !== action.payload) {
        state.scopeOfWork = null;
      }
      state.quoteMode = action.payload;
    },

    setEmployees: (state, action) => {
      state.workforce.employees = action.payload;
    },

    setContractors: (state, action) => {
      state.workforce.contractors = action.payload;
    },

    setSupervisors: (state, action) => {
      state.workforce.supervisors = action.payload;
    },

    setQuote: (state, action) => {
      const next = action.payload;
      if (state.quote?.id !== next?.id) {
        state.creditedDepositAmount = null;
        state.eventScheduler = null;
      }
      state.quote = next;
    },

    setLastCreatedJobId: (state, action) => {
      state.lastCreatedJobId = action.payload;
    },

    clearLastCreatedJobId: (state) => {
      state.lastCreatedJobId = null;
    },

    setScratchCreateAutoFlow: (state, action) => {
      state.scratchCreateAutoFlow = action.payload;
    },

    clearScratchCreateAutoFlow: (state) => {
      state.scratchCreateAutoFlow = null;
    },

    selectService: (state, action) => {
      const payload = action.payload;
      const prevUid = state.selectedService?.service?.serviceUid;
      state.selectedService = payload;

      if (!payload?.service) {
        state.eventScheduler = null;
        return;
      }

      const nextUid = payload.service.serviceUid;
      if (prevUid !== nextUid) {
        state.eventScheduler = null;
      }
    },

    clearSelectedQuote: (state) => {
      state.selectedService = null;
      state.quote = null;
    },

    setStaffErrors: (state, action) => {
      state.errors = action.payload;
    },

    clearStaffErrors: (state) => {
      state.errors = [];
    },

    setEventScheduler: (state, action) => {
      state.eventScheduler = action.payload;
    },

    setStep: (state, action) => {
      state.step = action.payload;
    },

    setAlias: (state, action) => {
      state.alias = action.payload;
    },

    setServiceNameOverride: (state, action) => {
      const value = action.payload;

      if (Array.isArray(state.scopeOfWork)) {
        if (!state.scopeOfWork[0]) {
          state.scopeOfWork[0] = {};
        }

        state.scopeOfWork[0].serviceNameOverride = value;
        return;
      }

      if (!state.scopeOfWork) {
        state.scopeOfWork = {};
      }

      state.scopeOfWork.serviceNameOverride = value;
    },

    nextStep: (state) => {
      state.step += 1;
    },

    prevStep: (state) => {
      if (state.step > 0) state.step -= 1;
    },

    setEditingFromSummary: (state, action) => {
      state.editingFromSummary = action.payload;
    },

    setReturnToSummaryAfterEdit: (state, action) => {
      state.returnToSummaryAfterEdit = action.payload;
    },

    restoreJobCreateSnapshot: (state, action) => {
      const lastCreatedJobId = state.lastCreatedJobId;
      const scratchCreateAutoFlow = state.scratchCreateAutoFlow;

      return {
        ...action.payload,
        lastCreatedJobId,
        scratchCreateAutoFlow,
      };
    },

    resetStepper: (state) => {
      state.step = 0;
      state.quote = null;
      state.selectedService = null;
      state.eventScheduler = null;
      state.errors = [];
    },

    setScheduleLater: (state, action) => {
      state.scheduleLater = action.payload;
    },

    resetJobCreateStepper: (state) => {
      const lastCreatedJobId = state.lastCreatedJobId;
      const scratchCreateAutoFlow = state.scratchCreateAutoFlow;

      return {
        ...initialState,
        lastCreatedJobId,
        scratchCreateAutoFlow,
      };
    },

    setReminderByCategory: (state, action) => {
      const { category, data } = action.payload;
      state.selectedReminders[category] = data;
    },

    setCreditedDepositAmount: (state, action) => {
      state.creditedDepositAmount = action.payload;
    },

    setTaskNoteInJobCreate: (state, action) => {
      const { areaUid, taskUid, note } = action.payload;

      const updateTree = (node) => {
        if (!node || typeof node !== "object") return node;
        if (Array.isArray(node)) return node.map((item) => updateTree(item));

        if (node.uid === areaUid && Array.isArray(node.tasks)) {
          return {
            ...node,
            tasks: node.tasks.map((task) => {
              const taskId = String(task?.uid ?? task?.id);
              return taskId === String(taskUid) ? { ...task, note } : task;
            }),
          };
        }

        const nextNode = { ...node };
        if (Array.isArray(node.areas)) {
          nextNode.areas = node.areas.map((item) => updateTree(item));
        }
        if (Array.isArray(node.otherAreas)) {
          nextNode.otherAreas = node.otherAreas.map((item) => updateTree(item));
        }
        return nextNode;
      };

      if (state.scopeOfWork) {
        state.scopeOfWork = updateTree(state.scopeOfWork);
      }

      if (state.selectedService?.service?.scopeOfWork) {
        state.selectedService.service.scopeOfWork = updateTree(state.selectedService.service.scopeOfWork);
      }

      if (state.selectedService?.scopeOfWork) {
        state.selectedService.scopeOfWork = updateTree(state.selectedService.scopeOfWork);
      }
    },
  },
});

export const {
  setClient,
  setSkipClientSelection,
  setScopeOfWork,
  setScopeOfWorkCostOverride,
  clearScopeOfWorkCostOverride,
  setEmployees,
  setContractors,
  setSupervisors,
  setQuote,
  selectService,
  setScopeOfWorkMode,
  clearSelectedQuote,
  setStaffErrors,
  clearStaffErrors,
  setEventScheduler,
  setStep,
  nextStep,
  prevStep,
  resetStepper,
  resetJobCreateStepper,
  setAlias,
  setServiceNameOverride,
  setScheduleLater,
  setReminderByCategory,
  setCreditedDepositAmount,
  setTaskNoteInJobCreate,
  setLastCreatedJobId,
  clearLastCreatedJobId,
  setScratchCreateAutoFlow,
  clearScratchCreateAutoFlow,
  setDescription,
  setNotes,
  setFrequencyType,
  restoreJobCreateSnapshot,
} = jobCreateStepper.actions;

export const selectJobCreateState = (state) => state.jobCreateStepper;
export default jobCreateStepper.reducer;
